import { marked } from 'marked';
import { markedHighlight } from 'marked-highlight';
import Prism from 'prismjs';
// Map of light image path -> dark image path (generated at prebuild from any
// "<name>-dark.png" in static/images). Used to render theme-aware screenshots.
import imageVariants from './image-variants.json';

// Import Prism languages
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-scss';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-docker';
import 'prismjs/components/prism-graphql';

// Configure marked with syntax highlighting
marked.use(
	markedHighlight({
		langPrefix: 'language-',
		highlight(code, lang) {
			if (lang && Prism.languages[lang]) {
				return Prism.highlight(code, Prism.languages[lang], lang);
			}
			return code;
		}
	})
);

// Custom renderer for enhanced features
const renderer = new marked.Renderer();

const darkVariants = imageVariants as Record<string, string>;

function escapeAttr(value: string): string {
	return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

// Theme-aware images: when a screenshot has a matching "<name>-dark" variant,
// emit both and let CSS (.doc-img-light / .doc-img-dark) show the right one for
// the reader's theme. Otherwise render a normal image.
renderer.image = function (token) {
	const href = typeof token === 'object' ? token.href : (arguments[0] as string);
	const titleRaw = typeof token === 'object' ? token.title : (arguments[1] as string);
	const text = typeof token === 'object' ? token.text : (arguments[2] as string);
	const alt = escapeAttr(text || '');
	const title = titleRaw ? ` title="${escapeAttr(titleRaw)}"` : '';
	const dark = href && darkVariants[href];
	if (dark) {
		return (
			`<img src="${escapeAttr(href)}" alt="${alt}"${title} class="doc-img-light" loading="lazy" />` +
			`<img src="${escapeAttr(dark)}" alt="${alt}"${title} class="doc-img-dark" loading="lazy" />`
		);
	}
	return `<img src="${escapeAttr(href)}" alt="${alt}"${title} loading="lazy" />`;
};

// Add IDs to headings for anchoring
renderer.heading = function(token) {
	// Handle both old and new marked API
	let text, level, raw;
	
	if (typeof token === 'object' && token.tokens) {
		// New API - token is an object
		text = this.parser.parseInline(token.tokens);
		level = token.depth;
		raw = token.text;
	} else {
		// Old API - arguments are passed separately
		text = arguments[0];
		level = arguments[1];
		raw = arguments[2];
	}
	
	// Ensure we have text to work with
	const headingText = (raw || text || '').toString();
	// Generate anchor the same way as in extractTableOfContents
	let cleanText = headingText;
	// Remove markdown formatting
	cleanText = cleanText.replace(/\*\*(.+?)\*\*/g, '$1'); // Bold
	cleanText = cleanText.replace(/\*(.+?)\*/g, '$1'); // Italic  
	cleanText = cleanText.replace(/__(.+?)__/g, '$1'); // Bold alt
	cleanText = cleanText.replace(/_(.+?)_/g, '$1'); // Italic alt
	cleanText = cleanText.replace(/`(.+?)`/g, '$1'); // Code
	cleanText = cleanText.replace(/\[(.+?)\]\(.+?\)/g, '$1'); // Links
	// Remove emojis
	cleanText = cleanText.replace(/[\u{1F300}-\u{1F9FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F1E0}-\u{1F1FF}]/gu, '');
	cleanText = cleanText.replace(/:[\w_]+:/g, '');
	cleanText = cleanText.trim().replace(/\s+/g, ' ');
	const anchor = cleanText.toLowerCase().replace(/[^\w]+/g, '-');
	const displayText = text || '';
	
	return `
		<h${level} id="${anchor}" class="docs-heading docs-heading--${level}">
			${displayText}
			<a href="#${anchor}" class="docs-heading__anchor" aria-label="Link to ${displayText}">
				<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
					<path d="M7.775 3.275a.75.75 0 001.06 1.06l1.25-1.25a2 2 0 112.83 2.83l-2.5 2.5a2 2 0 01-2.83 0 .75.75 0 00-1.06 1.06 3.5 3.5 0 004.95 0l2.5-2.5a3.5 3.5 0 00-4.95-4.95l-1.25 1.25zm-4.69 9.64a2 2 0 010-2.83l2.5-2.5a2 2 0 012.83 0 .75.75 0 001.06-1.06 3.5 3.5 0 00-4.95 0l-2.5 2.5a3.5 3.5 0 004.95 4.95l1.25-1.25a.75.75 0 00-1.06-1.06l-1.25 1.25a2 2 0 01-2.83 0z"/>
				</svg>
			</a>
		</h${level}>
	`;
};

// Simple hash function for deterministic IDs
function simpleHash(str: string): number {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash);
}

// Enhanced code blocks with copy button
renderer.code = function(token) {
	// Handle both old and new marked API
	let code, language;
	
	if (typeof token === 'object' && token.text) {
		// New API - token is an object
		code = token.text;
		language = token.lang;
	} else {
		// Old API - arguments are passed separately
		code = arguments[0];
		language = arguments[1];
	}
	
	// Generate deterministic ID based on code content
	const id = 'code-' + simpleHash(code || '');
	const highlightedCode = language && Prism.languages[language] 
		? Prism.highlight(code, Prism.languages[language], language)
		: code;
	
	return `
		<div class="docs-code-block" data-language="${language || 'text'}">
			<div class="docs-code-block__header">
				<span class="docs-code-block__language">${language || 'text'}</span>
				<button 
					class="docs-code-block__copy" 
					data-code-id="${id}"
					aria-label="Copy code"
					title="Copy code"
				>
					<svg class="docs-code-block__copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
						<path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 019.25 16h-7.5A1.75 1.75 0 010 14.25v-7.5z"/>
						<path d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z"/>
					</svg>
					<span class="docs-code-block__copy-text">Copy</span>
				</button>
			</div>
			<pre class="docs-code-block__pre"><code id="${id}" class="language-${language || 'text'}">${highlightedCode}</code></pre>
		</div>
	`;
};

// Enhanced links - external links open in new tab
renderer.link = function(token) {
	// Handle both old and new marked API
	let href, title, text;
	
	if (typeof token === 'object' && token.href) {
		// New API - token is an object
		href = token.href;
		title = token.title;
		text = this.parser.parseInline(token.tokens);
	} else {
		// Old API - arguments are passed separately
		href = arguments[0];
		title = arguments[1];
		text = arguments[2];
	}
	
	// Ensure href is a string
	href = (href || '').toString();
	const isExternal = href.startsWith('http') && !href.includes(typeof window !== 'undefined' ? window.location.hostname : '');
	const titleAttr = title ? ` title="${title}"` : '';
	const targetAttr = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';
	const externalIcon = isExternal ? `
		<svg class="docs-link__external" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
			<path d="M10.604 1h-3.897a.396.396 0 100 .792h2.826L4.771 6.554a.396.396 0 10.56.56l4.762-4.762v2.826a.396.396 0 00.792 0V1.396A.396.396 0 0010.604 1z"/>
			<path d="M8.416 8.416H2.98V2.98h2.643a.396.396 0 000-.792H2.584a.396.396 0 00-.396.396v6.832a.396.396 0 00.396.396h6.832a.396.396 0 00.396-.396V6.376a.396.396 0 00-.792 0v2.04h-.604z"/>
		</svg>
	` : '';
	
	return `<a href="${href}"${titleAttr}${targetAttr} class="docs-link">${text}${externalIcon}</a>`;
};

// Enhanced tables with better styling
renderer.table = function(token) {
	// marked v16: token.header is an array of cell tokens and token.rows is an
	// array of rows, each itself an array of cell tokens. Render each cell with
	// tablecell (handles th/td + inline markdown), then wrap with tablerow.
	// (The previous code passed the cell arrays straight to tablerow, whose
	// {text} destructuring yielded `undefined` for every row.)
	let headerCells = '';
	for (const cell of token.header) {
		headerCells += this.tablecell(cell);
	}
	const header = this.tablerow({ text: headerCells });

	let body = '';
	for (const row of token.rows) {
		let cells = '';
		for (const cell of row) {
			cells += this.tablecell(cell);
		}
		body += this.tablerow({ text: cells });
	}

	return `
		<div class="docs-table-wrapper">
			<table class="docs-table">
				<thead class="docs-table__head">
					${header}
				</thead>
				<tbody class="docs-table__body">
					${body}
				</tbody>
			</table>
		</div>
	`;
};

// Enhanced blockquotes with different types
renderer.blockquote = function(token) {
	// Handle both old and new marked API
	let quote;
	
	if (typeof token === 'object' && token.tokens) {
		// New API - token is an object
		quote = this.parser.parse(token.tokens);
	} else {
		// Old API - arguments are passed separately
		quote = arguments[0];
	}
	// Check for GitHub-style callout markers ([!NOTE], [!TIP], etc.) on the RAW
	// blockquote text. `quote` is already-rendered HTML (starts with <p>…), so
	// matching it against /^\[!/ never works — marked exposes the raw inner
	// markdown on token.text, so detect there and strip the marker from the HTML.
	const rawText =
		token && typeof token === 'object' && typeof token.text === 'string' ? token.text : quote;
	const typeMatch = rawText.match(/^\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]/);
	if (typeMatch) {
		const type = typeMatch[1].toLowerCase();
		const content = quote.replace(/\[!(?:NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/, '');
		const icons = {
			note: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm6.5-.25A.75.75 0 017.25 7h1a.75.75 0 01.75.75v2.75h.25a.75.75 0 010 1.5h-2a.75.75 0 010-1.5h.25v-2h-.25a.75.75 0 01-.75-.75zM8 6a1 1 0 100-2 1 1 0 000 2z"/></svg>',
			tip: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1.5c-2.363 0-4 1.69-4 3.75 0 .984.424 1.625.984 2.304l.214.253c.223.264.47.556.673.848.284.411.537.896.537 1.345 0 .272 0 .5-.051.672-.056.188-.139.312-.25.422-.108.108-.233.194-.422.25-.172.051-.4.051-.672.051a.75.75 0 00-.75.75c0 .212.03.414.088.605.058.19.155.376.303.524.236.236.554.357.895.421.339.064.713.064 1.05.064h.834c.337 0 .711 0 1.05-.064.341-.064.659-.185.895-.421a1.31 1.31 0 00.303-.524c.058-.191.088-.393.088-.605a.75.75 0 00-.75-.75c-.272 0-.5 0-.672-.051a1.059 1.059 0 01-.422-.25 1.06 1.06 0 01-.25-.422C8.5 10 8.5 9.772 8.5 9.5c0-.449.253-.934.537-1.345.203-.292.45-.584.673-.848l.214-.253c.56-.679.984-1.32.984-2.304 0-2.06-1.637-3.75-4-3.75zM5.75 12h4.5a.25.25 0 00.25-.25v-.5a.25.25 0 00-.25-.25h-4.5a.25.25 0 00-.25.25v.5c0 .138.112.25.25.25z"/></svg>',
			important: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0a8 8 0 100 16A8 8 0 008 0zM7 3.5a1 1 0 112 0v5a1 1 0 01-2 0v-5zm1 9.5a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z"/></svg>',
			warning: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8.982 1.566a1.13 1.13 0 00-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 01-1.1 0L7.1 5.995A.905.905 0 018 5zm.002 6a1 1 0 100 2 1 1 0 000-2z"/></svg>',
			caution: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M4.47.22A.75.75 0 015 0h6a.75.75 0 01.53.22l4.25 4.25c.141.14.22.331.22.53v6a.75.75 0 01-.22.53l-4.25 4.25A.75.75 0 0111 16H5a.75.75 0 01-.53-.22L.22 11.53A.75.75 0 010 11V5a.75.75 0 01.22-.53L4.47.22zM7.25 4a.75.75 0 00-.75.75v2.5a.75.75 0 001.5 0v-2.5A.75.75 0 007.25 4zM8 10.5a1 1 0 100 2 1 1 0 000-2z"/></svg>'
		};
		
		return `
			<blockquote class="docs-callout docs-callout--${type}">
				<div class="docs-callout__header">
					<span class="docs-callout__icon">${icons[type] || icons.note}</span>
					<span class="docs-callout__title">${typeMatch[1]}</span>
				</div>
				<div class="docs-callout__content">${content}</div>
			</blockquote>
		`;
	}
	
	return `<blockquote class="docs-quote">${quote}</blockquote>`;
};

// Configure marked
marked.setOptions({
	renderer,
	gfm: true,
	breaks: true,
	pedantic: false,
	smartLists: true,
	smartypants: true
});

// Process markdown with enhanced features
export function processMarkdown(content: string): string {
	return marked(content);
}

// Extract table of contents from markdown
export function extractTableOfContents(content: string) {
	const headings: Array<{ level: number; text: string; anchor: string }> = [];
	const tokens = marked.lexer(content);
	
	tokens.forEach((token: any) => {
		if (token.type === 'heading' && token.depth >= 2 && token.depth <= 4) {
			// Remove markdown formatting from text
			let text = token.text;
			// Remove bold/italic markers
			text = text.replace(/\*\*(.+?)\*\*/g, '$1'); // Bold
			text = text.replace(/\*(.+?)\*/g, '$1'); // Italic
			text = text.replace(/__(.+?)__/g, '$1'); // Bold alt
			text = text.replace(/_(.+?)_/g, '$1'); // Italic alt
			text = text.replace(/`(.+?)`/g, '$1'); // Code
			text = text.replace(/\[(.+?)\]\(.+?\)/g, '$1'); // Links
			// Remove emojis (Unicode emoji ranges)
			text = text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F1E0}-\u{1F1FF}]/gu, '');
			// Remove emoji shortcodes
			text = text.replace(/:[\w_]+:/g, '');
			// Clean up extra spaces
			text = text.trim().replace(/\s+/g, ' ');
			
			const anchor = text.toLowerCase().replace(/[^\w]+/g, '-');
			headings.push({
				level: token.depth,
				text,
				anchor
			});
		}
	});
	
	return headings;
}

// Initialize copy functionality
export function initializeCopyButtons() {
	if (typeof document === 'undefined') return;
	
	document.addEventListener('click', (e) => {
		const target = e.target as HTMLElement;
		const button = target.closest('.docs-code-block__copy');
		
		if (button) {
			const codeId = button.getAttribute('data-code-id');
			const codeElement = document.getElementById(codeId!);
			
			if (codeElement) {
				const code = codeElement.textContent || '';
				navigator.clipboard.writeText(code).then(() => {
					const textElement = button.querySelector('.docs-code-block__copy-text');
					if (textElement) {
						textElement.textContent = 'Copied!';
						setTimeout(() => {
							textElement.textContent = 'Copy';
						}, 2000);
					}
				});
			}
		}
	});
}